import { useMemo, useRef, useState } from 'react';
import type { KspCollectionMeta, KspWorkspace } from './types';
import { useLazyGetKspCollectionQuery } from '@shared/api/endpoints/ksp';

/** Additional rows belong to one exact workspace revision and historical scope. */
export function useKspCollections(
  source: KspWorkspace | undefined,
  args: { projectId: string; versionId?: string; snapshotId?: string },
) {
  const snapshotId =
    args.snapshotId ??
    (source?.history_read_only ? (source.selected_snapshot_id ?? undefined) : undefined);
  const identity = `${args.projectId}|${args.versionId ?? source?.selected_version_id ?? ''}|${snapshotId ?? ''}|${source?.version ?? ''}`;
  const current = useRef(identity);
  current.current = identity;
  const [fetchPage] = useLazyGetKspCollectionQuery();
  const [loaded, setLoaded] = useState<{
    identity: string;
    rows: Record<string, Record<string, unknown>[]>;
    meta: Record<string, KspCollectionMeta>;
  }>({ identity: '', rows: {}, meta: {} });
  const [pending, setPending] = useState<{ identity: string; collection: string } | null>(null);
  const workspace = useMemo(() => {
    if (!source || loaded.identity !== identity) return source;
    const merged = { ...source, collections: { ...source.collections, ...loaded.meta } };
    const records = merged as unknown as Record<string, unknown>;
    for (const [key, rows] of Object.entries(loaded.rows)) {
      const initial = (records[key] as Record<string, unknown>[]) ?? [];
      const ids = new Set(initial.map((row) => row.id));
      records[key] = [...initial, ...rows.filter((row) => !row.id || !ids.has(row.id))];
    }
    return merged;
  }, [source, loaded, identity]);
  const loadMore = async (collection: string) => {
    const meta = workspace?.collections?.[collection];
    if (!meta?.truncated || pending?.identity === identity) return;
    if (
      source?.history_read_only &&
      !snapshotId &&
      !['activities', 'dependencies', 'calendars', 'resources', 'wbs'].includes(collection)
    )
      throw new Error(
        'Для подгрузки исторических данных требуется сохранённый снимок. Обновите выбранный срез.',
      );
    const offset = meta.offset + meta.returned;
    setPending({ identity, collection });
    try {
      const page = await fetchPage(
        {
          ...args,
          snapshotId,
          versionId: args.versionId ?? source?.selected_version_id ?? undefined,
          collection,
          offset,
          limit: 5000,
        },
        false,
      ).unwrap();
      if (current.current !== identity) return;
      if (source?.history_read_only && page.history_read_only !== true)
        throw new Error(
          'Сервер вернул текущую страницу для исторического среза. Данные не добавлены.',
        );
      if (page.offset !== offset || (page.truncated && page.returned === 0))
        throw new Error('Сервер вернул несогласованную страницу. Обновите данные проекта.');
      setLoaded((previous) => {
        const previousRows = previous.identity === identity ? previous.rows : {};
        const previousMeta = previous.identity === identity ? previous.meta : {};
        return {
          identity,
          rows: {
            ...previousRows,
            [collection]: [...(previousRows[collection] ?? []), ...page.rows],
          },
          meta: {
            ...previousMeta,
            [collection]: { ...page, offset: 0, returned: offset + page.returned },
          },
        };
      });
    } finally {
      setPending((value) => (value?.identity === identity ? null : value));
    }
  };
  return {
    workspace,
    loadMore,
    loading: pending?.identity === identity ? pending.collection : null,
  };
}
